import React, { useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, Heart, MessageCircle, Repeat } from 'react-feather';
import { XIcon } from './XIcon';
import { integrationsAPI, CoachTweet } from '../services/integrationsAPI';

interface CoachTweetsSectionProps {
  coachId: number;
  coachName: string;
}

const CoachTweetsSection: React.FC<CoachTweetsSectionProps> = ({ coachId, coachName }) => {
  const [expanded, setExpanded] = useState(false);
  const [tweets, setTweets] = useState<CoachTweet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchTweets = async () => {
    if (hasFetched) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await integrationsAPI.getCoachTweets(coachId);
      if (response.success && response.data.tweets) {
        setTweets(response.data.tweets);
      }
      setHasFetched(true);
    } catch (err) {
      console.error('Error fetching coach tweets:', err);
      setError('Unable to load tweets');
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!expanded && !hasFetched) {
      fetchTweets();
    }
    setExpanded(!expanded);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const openTweet = (tweet: CoachTweet) => {
    const url = `https://twitter.com/${tweet.author_username}/status/${tweet.tweet_id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ marginTop: '16px', borderTop: '1px solid #E5E7EB', paddingTop: '16px' }}>
      <button
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <XIcon size={16} color="#000000" />
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
            Latest Tweets
          </span>
        </div>
        {expanded ? (
          <ChevronUp size={18} color="#6B7280" />
        ) : (
          <ChevronDown size={18} color="#6B7280" />
        )}
      </button>

      {expanded && (
        <div style={{ marginTop: '12px' }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px', justifyContent: 'center' }}>
              <RefreshCw size={16} color="#6B7280" className="spin-animation" />
              <span style={{ fontSize: '14px', color: '#6B7280' }}>Loading tweets...</span>
            </div>
          )}

          {error && (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <p style={{ fontSize: '14px', color: '#9CA3AF', margin: 0 }}>{error}</p>
            </div>
          )}

          {!loading && !error && tweets.length === 0 && hasFetched && (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <p style={{ fontSize: '14px', color: '#9CA3AF', margin: 0 }}>No recent tweets available</p>
            </div>
          )}

          {!loading && tweets.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {tweets.map((tweet) => (
                <div
                  key={tweet.tweet_id}
                  onClick={() => openTweet(tweet)}
                  style={{
                    padding: '12px',
                    background: '#F9FAFB',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    border: '1px solid #E5E7EB',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F3F4F6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F9FAFB';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                      @{tweet.author_username}
                    </span>
                    <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                      · {formatDate(tweet.created_at)}
                    </span>
                  </div>
                  <p style={{ 
                    fontSize: '14px', 
                    color: '#374151', 
                    margin: '0 0 8px 0',
                    lineHeight: '1.5',
                    wordBreak: 'break-word',
                  }}>
                    {tweet.text}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {tweet.reply_count > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MessageCircle size={14} color="#6B7280" />
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>{tweet.reply_count}</span>
                      </div>
                    )}
                    {tweet.retweet_count > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Repeat size={14} color="#6B7280" />
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>{tweet.retweet_count}</span>
                      </div>
                    )}
                    {tweet.like_count > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Heart size={14} color="#6B7280" />
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>{tweet.like_count}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`
        .spin-animation {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CoachTweetsSection;
